import {
  WIDGET_DEFAULT_CAROUSEL_INTERVAL_SECONDS,
  WIDGET_DEFAULT_CAROUSEL_TRANSITION,
  type WidgetCarouselTransition,
} from "@shared/widgets/values";
import type { WidgetPublicReview } from "@shared/contracts/widgets/public/common";
import { renderReviewCard } from "../components/reviewCard";
import type { Translator } from "../i18n/resolve";
import { escapeAttr } from "../utils/escape";

const PAUSE_ICON = "❚❚";
const PLAY_ICON = "▶";

export type CarouselInput = {
  reviews: ReadonlyArray<WidgetPublicReview>;
  translate: Translator;
  now?: number;
  transition?: WidgetCarouselTransition;
};

export const renderCarousel = ({
  reviews,
  translate,
  now,
  transition = WIDGET_DEFAULT_CAROUSEL_TRANSITION,
}: CarouselInput): string => {
  const total = reviews.length;
  const slides = reviews
    .map((review, index) => {
      const isActive = index === 0;
      const slideLabel = translate("carouselSlideAria", { index: index + 1, total });
      const attrs = `data-mbn-slide="${index}" role="group" aria-roledescription="slide" aria-label="${escapeAttr(slideLabel)}" aria-hidden="${isActive ? "false" : "true"}"`;
      return renderReviewCard({ review, translate, now, slideAttributes: attrs });
    })
    .join("");
  const label = escapeAttr(translate("wallLabel"));
  const carouselDesc = escapeAttr(translate("wallLabel"));
  const playLabel = escapeAttr(translate("carouselPause"));
  return `<section class="mbn-carousel" role="region" aria-roledescription="carousel" aria-label="${label}" aria-description="${carouselDesc}" tabindex="0"><div class="mbn-slides" aria-live="polite" data-mbn-live data-transition="${escapeAttr(transition)}">${slides}</div><div class="mbn-controls"><button type="button" class="mbn-prev" data-mbn-prev aria-label="${escapeAttr(
    translate("carouselPrev"),
  )}">‹</button><button type="button" class="mbn-toggle" data-mbn-toggle aria-pressed="true" aria-label="${playLabel}">${PAUSE_ICON}</button><button type="button" class="mbn-next" data-mbn-next aria-label="${escapeAttr(
    translate("carouselNext"),
  )}">›</button></div></section>`;
};

export type CarouselController = {
  destroy: () => void;
};

export type CarouselWiringInput = {
  root: ShadowRoot | Element;
  translate: Translator;
  intervalMs?: number;
};

export const wireCarousel = ({
  root,
  translate,
  intervalMs = WIDGET_DEFAULT_CAROUSEL_INTERVAL_SECONDS * 1000,
}: CarouselWiringInput): CarouselController | null => {
  const carousel = root.querySelector(".mbn-carousel");
  if (!carousel) return null;
  const slides = Array.from(carousel.querySelectorAll<HTMLElement>("[data-mbn-slide]"));
  const toggle = carousel.querySelector<HTMLButtonElement>("[data-mbn-toggle]");
  const prev = carousel.querySelector<HTMLButtonElement>("[data-mbn-prev]");
  const next = carousel.querySelector<HTMLButtonElement>("[data-mbn-next]");
  if (slides.length === 0) return null;

  let activeIndex = 0;
  let timer: ReturnType<typeof setInterval> | null = null;
  let isPlaying = true;
  let isPausedByKeyboardFocus = false;

  const setActive = (index: number) => {
    activeIndex = ((index % slides.length) + slides.length) % slides.length;
    slides.forEach((slide, idx) => {
      slide.setAttribute("aria-hidden", idx === activeIndex ? "false" : "true");
    });
  };

  const stop = () => {
    if (timer === null) return;
    clearInterval(timer);
    timer = null;
  };

  const restart = () => {
    stop();
    if (isPlaying) timer = setInterval(() => setActive(activeIndex + 1), intervalMs);
  };

  const setPlaying = (playing: boolean) => {
    isPlaying = playing;
    if (toggle) {
      toggle.setAttribute("aria-pressed", playing ? "true" : "false");
      toggle.setAttribute(
        "aria-label",
        playing ? translate("carouselPause") : translate("carouselPlay"),
      );
      toggle.textContent = playing ? PAUSE_ICON : PLAY_ICON;
    }
    restart();
  };

  const goTo = (index: number) => {
    setActive(index);
    restart();
  };

  const togglePlaying = () => {
    isPausedByKeyboardFocus = false;
    setPlaying(!isPlaying);
  };

  const handleFocusIn = (event: FocusEvent) => {
    const isKeyboardFocus = (event.target as Element).matches(":focus-visible");
    if (!isKeyboardFocus || !isPlaying) return;
    isPausedByKeyboardFocus = true;
    setPlaying(false);
  };
  const handleFocusOut = (event: FocusEvent) => {
    const related = event.relatedTarget as Node | null;
    if (!isPausedByKeyboardFocus || (related && carousel.contains(related))) return;
    isPausedByKeyboardFocus = false;
    setPlaying(true);
  };

  const handleKey = (event: KeyboardEvent) => {
    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        goTo(activeIndex + 1);
        break;
      case "ArrowLeft":
        event.preventDefault();
        goTo(activeIndex - 1);
        break;
      case "Home":
        event.preventDefault();
        goTo(0);
        break;
      case "End":
        event.preventDefault();
        goTo(slides.length - 1);
        break;
      case " ":
      case "Spacebar":
        event.preventDefault();
        togglePlaying();
        break;
      default:
        break;
    }
  };

  const handlePrevClick = () => goTo(activeIndex - 1);
  const handleNextClick = () => goTo(activeIndex + 1);

  carousel.addEventListener("focusin", handleFocusIn as EventListener);
  carousel.addEventListener("focusout", handleFocusOut as EventListener);
  carousel.addEventListener("keydown", handleKey as EventListener);
  toggle?.addEventListener("click", togglePlaying);
  prev?.addEventListener("click", handlePrevClick);
  next?.addEventListener("click", handleNextClick);

  restart();

  return {
    destroy: () => {
      stop();
      carousel.removeEventListener("focusin", handleFocusIn as EventListener);
      carousel.removeEventListener("focusout", handleFocusOut as EventListener);
      carousel.removeEventListener("keydown", handleKey as EventListener);
      toggle?.removeEventListener("click", togglePlaying);
      prev?.removeEventListener("click", handlePrevClick);
      next?.removeEventListener("click", handleNextClick);
    },
  };
};
